import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
  Tab,
  TabBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@dhis2/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";
import useMetadataStore from "@/state/metadata";
import { pickTranslation, convertDataByDataType } from "@/config/utils";
import { t } from "i18next";
import Map from "./Map";
import { Box, Skeleton } from "@mui/material";
import "./index.css";
const PROGRAM_STAGE_MAPPING = {
  Uy3UPUWJSFF: "primaryDetails",
  ATYFvq36gIh: "accessDetails",
  k7aLsCn5hpD: "infrastructureDetails",
  LSZjNuZGXY7: "staffDetails",
  jPlvUj21OkV: "serviceDetails",
  gRrr92F5qEV: "keyData",
};
const PROFILE_TAB = {
  name: t("profile"),
  id: "profile",
  translations: [],
};

const PRIMARY_DETAILS = "primaryDetails";
const PHOTO_URL = "CCiO6CEBipb";

const MaterFacilityDetail = ({ facility, onClose }) => {
  const { t, i18n } = useTranslation();
  const { serviceDeliveryProgram, orgUnits } = useMetadataStore(
    useShallow((state) => ({
      serviceDeliveryProgram: state.oupProgram,
      orgUnits: state.oupOrgUnits,
    }))
  );
  const { programStages, dataElements, trackedEntityAttributes } =
    serviceDeliveryProgram;
  const [selectedStage, setSelectedStage] = useState(PROFILE_TAB);
  const [loadingImage, setLoadingImage] = useState(true);
  const handleClose = () => {
    onClose();
    setSelectedStage(PROFILE_TAB);
  };

  const sortedTrackedEntityAttributes = useMemo(() => {
    return [...trackedEntityAttributes].sort((a, b) => {
      const displayNameA =
        pickTranslation(a, i18n.language, "formName") ||
        pickTranslation(a, i18n.language, "name");
      const displayNameB =
        pickTranslation(b, i18n.language, "formName") ||
        pickTranslation(b, i18n.language, "name");
      if (displayNameA > displayNameB) return 1;
      if (displayNameA < displayNameB) return -1;
      return 0;
    });
  }, []);
  const foundDataValues = useMemo(
    () =>
      facility.tei && selectedStage.id !== "profile"
        ? facility[PROGRAM_STAGE_MAPPING[selectedStage.id]][0]
        : {},
    [facility.tei, selectedStage.id]
  );

  const map = useMemo(() => <Map tei={facility.tei} />, [facility.tei]);
  if (!facility.tei) return null;

  return (
    <>
      <Modal
        className="detail-modal"
        position="middle"
        hide={!facility.tei}
        onClose={handleClose}
      >
        <ModalTitle>{t("detail")}</ModalTitle>
        <ModalContent>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 2,
              height: "calc(-264px + 100vh)",
            }}
          >
            <Box
              sx={{
                width: "55%",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 4,
                "& .detail-tab": {
                  position: "sticky",
                  top: "0",
                },
              }}
            >
              <TabBar className="detail-tab" scrollable>
                {[
                  { name: t("profile"), id: "profile", translations: [] },
                  ...[...programStages].sort(
                    (a, b) => a.sortOrder - b.sortOrder
                  ),
                ].map((stage) => (
                  <Tab
                    key={stage.id}
                    onClick={() => {
                      setSelectedStage(stage);
                    }}
                    selected={selectedStage.id === stage.id}
                  >
                    {pickTranslation(stage, i18n.language, "name")}
                  </Tab>
                ))}
              </TabBar>
              <Box
                sx={{
                  flex: 1,
                  "& td": { paddingY: "0px !important" },
                  "& thead td": { fontWeight: "700", fontSize: "16px" },
                }}
              >
                {selectedStage.id === "profile" ? (
                  <Table>
                    <TableBody>
                      {sortedTrackedEntityAttributes.map((tea) => {
                        return (
                          <TableRow key={tea.id}>
                            <TableCell>
                              {pickTranslation(
                                tea,
                                i18n.language,
                                "formName"
                              ) || pickTranslation(tea, i18n.language, "name")}
                            </TableCell>
                            <TableCell>
                              {convertDataByDataType(facility[tea.id], tea.id, {
                                ...serviceDeliveryProgram,
                                orgUnits,
                              })}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Table>
                    <TableBody>
                      {selectedStage.programStageDataElements.map((psde) => {
                        const foundDataElement = dataElements.find(
                          (de) => de.id === psde.dataElement.id
                        );
                        return (
                          <TableRow key={psde.dataElement.id}>
                            <TableCell>
                              {pickTranslation(
                                foundDataElement,
                                i18n.language,
                                "formName"
                              ) ||
                                pickTranslation(
                                  foundDataElement,
                                  i18n.language,
                                  "name"
                                )}
                            </TableCell>
                            <TableCell>
                              {convertDataByDataType(
                                foundDataValues[foundDataElement.id],
                                foundDataElement.id,
                                {
                                  ...serviceDeliveryProgram,
                                  orgUnits,
                                }
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                width: "45%",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 300,
                  border: 1,
                  borderColor: "grey.400",
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {facility[PRIMARY_DETAILS]?.[0]?.[PHOTO_URL] ? (
                  <>
                    <Skeleton
                      variant="rectangular"
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: loadingImage ? "block" : "none",
                      }}
                      animation="wave"
                    />
                    <a
                      style={{
                        width: "100%",
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                        display: !loadingImage ? "flex" : "none",
                      }}
                      href={facility[PRIMARY_DETAILS]?.[0]?.[PHOTO_URL]}
                      target="_blank"
                    >
                      <img
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        src={facility[PRIMARY_DETAILS]?.[0]?.[PHOTO_URL]}
                        onLoad={() => {
                          setLoadingImage(false);
                        }}
                      />
                    </a>
                  </>
                ) : (
                  t("photoNotAvailable")
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 400,
                  border: 1,
                  borderColor: "grey.400",
                  borderRadius: 1,
                }}
              >
                {map}
              </Box>
            </Box>
          </Box>
        </ModalContent>
        <ModalActions>
          <Button primary onClick={handleClose}>
            {t("close")}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default MaterFacilityDetail;
